/**
 * TemplateIntakeForm - Dynamic Intake Questions Form
 * ============================================================================
 * DC-1078: Form component for gathering intake answers before assessment
 *
 * Features:
 * - Dynamic form rendering based on template.intake_questions
 * - Support for text, select, multiselect, boolean, range inputs
 * - Real-time validation with error display
 * - Progress indicator
 * - Help text expansion
 *
 * Pattern: Follows ManualDatasetForm.tsx
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type {
  UseCaseTemplate,
  IntakeQuestion,
  IntakeAnswerValue,
} from './types';
import { validateIntakeAnswers } from './useUseCaseTemplates';
import './TemplateIntakeForm.css';

// ============================================================================
// Types
// ============================================================================

export interface TemplateIntakeFormProps {
  /** Template with intake questions */
  template: UseCaseTemplate;
  /** Submit handler with answers */
  onSubmit: (answers: Record<string, IntakeAnswerValue>) => void;
  /** Cancel handler */
  onCancel: () => void;
  /** Initial answers (for editing) */
  initialAnswers?: Record<string, IntakeAnswerValue>;
  /** Form is being submitted */
  isSubmitting?: boolean;
  /** Optional CSS class */
  className?: string;
}

interface FieldErrors {
  [questionId: string]: string;
}

// ============================================================================
// Question Renderers
// ============================================================================

interface QuestionProps {
  question: IntakeQuestion;
  value: IntakeAnswerValue | undefined;
  onChange: (value: IntakeAnswerValue) => void;
  error?: string;
  disabled?: boolean;
}

const TextQuestion: React.FC<QuestionProps> = ({
  question,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div className="tif-field">
    <input
      type="text"
      id={question.id}
      className={`tif-input ${error ? 'tif-input--error' : ''}`}
      value={(value as string) || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`Enter ${question.question.toLowerCase().replace(/\?$/, '')}`}
      disabled={disabled}
    />
  </div>
);

const SelectQuestion: React.FC<QuestionProps> = ({
  question,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div className="tif-field">
    <select
      id={question.id}
      className={`tif-select ${error ? 'tif-select--error' : ''}`}
      value={(value as string) || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">Select an option...</option>
      {question.options?.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const MultiselectQuestion: React.FC<QuestionProps> = ({
  question,
  value,
  onChange,
  error,
  disabled,
}) => {
  const selectedValues = (value as string[]) || [];

  const toggleOption = (optionValue: string) => {
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((v) => v !== optionValue));
    } else {
      onChange([...selectedValues, optionValue]);
    }
  };

  return (
    <div className={`tif-multiselect ${error ? 'tif-multiselect--error' : ''}`}>
      {question.options?.map((option) => (
        <label
          key={option.value}
          className={`tif-multiselect-option ${
            selectedValues.includes(option.value) ? 'tif-multiselect-option--selected' : ''
          } ${disabled ? 'tif-multiselect-option--disabled' : ''}`}
        >
          <input
            type="checkbox"
            checked={selectedValues.includes(option.value)}
            onChange={() => toggleOption(option.value)}
            disabled={disabled}
          />
          <span className="tif-multiselect-label">{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const BooleanQuestion: React.FC<QuestionProps> = ({
  question: _question,
  value,
  onChange,
  error,
  disabled,
}) => {
  const boolValue = value === true;

  return (
    <div className="tif-toggle-container">
      <button
        type="button"
        className={`tif-toggle ${boolValue ? 'tif-toggle--on' : 'tif-toggle--off'} ${
          error ? 'tif-toggle--error' : ''
        }`}
        onClick={() => onChange(!boolValue)}
        disabled={disabled}
        aria-pressed={boolValue}
      >
        <span className="tif-toggle-track">
          <span className="tif-toggle-thumb" />
        </span>
        <span className="tif-toggle-label">{boolValue ? 'Yes' : 'No'}</span>
      </button>
    </div>
  );
};

const RangeQuestion: React.FC<QuestionProps> = ({
  question,
  value,
  onChange,
  error,
  disabled,
}) => {
  const min = question.validation?.min ?? 0;
  const max = question.validation?.max ?? 100;
  const numValue = (value as number) ?? min;

  return (
    <div className="tif-range-container">
      <input
        type="range"
        id={question.id}
        className={`tif-range ${error ? 'tif-range--error' : ''}`}
        min={min}
        max={max}
        value={numValue}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        disabled={disabled}
      />
      <div className="tif-range-labels">
        <span className="tif-range-min">{min}</span>
        <span className="tif-range-value">{numValue}</span>
        <span className="tif-range-max">{max}</span>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const TemplateIntakeForm: React.FC<TemplateIntakeFormProps> = ({
  template,
  onSubmit,
  onCancel,
  initialAnswers = {},
  isSubmitting = false,
  className = '',
}) => {
  // State
  const [answers, setAnswers] = useState<Record<string, IntakeAnswerValue>>(initialAnswers);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [validating, setValidating] = useState(false);
  const [showHelpFor, setShowHelpFor] = useState<string | null>(null);

  // Initialize default values
  useEffect(() => {
    const defaults: Record<string, IntakeAnswerValue> = {};
    template.intake_questions.forEach((q) => {
      if (q.default_value !== undefined && answers[q.id] === undefined) {
        defaults[q.id] = q.default_value;
      }
    });
    if (Object.keys(defaults).length > 0) {
      setAnswers((prev) => ({ ...defaults, ...prev }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Only apply defaults once on mount
  }, [template.intake_questions]);

  // Calculate progress
  const progress = useMemo(() => {
    const requiredQuestions = template.intake_questions.filter((q) => q.required);
    const answeredRequired = requiredQuestions.filter((q) => {
      const answer = answers[q.id];
      if (answer === undefined || answer === null || answer === '') return false;
      if (Array.isArray(answer) && answer.length === 0) return false;
      return true;
    });
    return {
      answered: answeredRequired.length,
      total: requiredQuestions.length,
      percentage: requiredQuestions.length > 0
        ? Math.round((answeredRequired.length / requiredQuestions.length) * 100)
        : 100,
    };
  }, [answers, template.intake_questions]);

  // Handle answer change
  const handleAnswerChange = useCallback(
    (questionId: string, value: IntakeAnswerValue) => {
      setAnswers((prev) => ({ ...prev, [questionId]: value }));
      // Clear error when user modifies the field
      if (errors[questionId]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[questionId];
          return next;
        });
      }
    },
    [errors]
  );

  // Validate and submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setValidating(true);
    try {
      const result = await validateIntakeAnswers(template.id, answers);

      if (!result.valid && result.errors) {
        setErrors(result.errors);
        // Scroll to first error
        const firstErrorId = Object.keys(result.errors)[0];
        const element = document.getElementById(`question-${firstErrorId}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      setErrors({});
      onSubmit(answers);
    } catch (err) {
      console.error('[TemplateIntakeForm] Validation error:', err);
      // Fall back to local validation
      const localErrors: FieldErrors = {};
      template.intake_questions.forEach((q) => {
        if (q.required) {
          const answer = answers[q.id];
          if (answer === undefined || answer === null || answer === '') {
            localErrors[q.id] = 'This field is required';
          } else if (Array.isArray(answer) && answer.length === 0) {
            localErrors[q.id] = 'Please select at least one option';
          }
        }
      });

      if (Object.keys(localErrors).length > 0) {
        setErrors(localErrors);
        return;
      }

      onSubmit(answers);
    } finally {
      setValidating(false);
    }
  };

  // Render question
  const renderQuestion = (question: IntakeQuestion) => {
    const value = answers[question.id];
    const error = errors[question.id];
    const showHelp = showHelpFor === question.id;

    const commonProps: QuestionProps = {
      question,
      value,
      onChange: (v) => handleAnswerChange(question.id, v),
      error,
      disabled: isSubmitting || validating,
    };

    return (
      <div
        key={question.id}
        id={`question-${question.id}`}
        className={`tif-question ${error ? 'tif-question--error' : ''}`}
      >
        <div className="tif-question-header">
          <label htmlFor={question.id} className="tif-question-label">
            {question.question}
            {question.required && <span className="tif-required">*</span>}
          </label>
          {question.help_text && (
            <button
              type="button"
              className="tif-help-toggle"
              onClick={() => setShowHelpFor(showHelp ? null : question.id)}
              aria-label="Toggle help"
            >
              ?
            </button>
          )}
        </div>

        {showHelp && question.help_text && (
          <div className="tif-help-text">{question.help_text}</div>
        )}

        {question.type === 'text' && <TextQuestion {...commonProps} />}
        {question.type === 'select' && <SelectQuestion {...commonProps} />}
        {question.type === 'multiselect' && <MultiselectQuestion {...commonProps} />}
        {question.type === 'boolean' && <BooleanQuestion {...commonProps} />}
        {question.type === 'range' && <RangeQuestion {...commonProps} />}

        {error && <div className="tif-error-message">{error}</div>}
      </div>
    );
  };

  return (
    <form className={`tif-form ${className}`} onSubmit={handleSubmit}>
      {/* Header */}
      <div className="tif-header">
        <h3 className="tif-title">{template.name}</h3>
        <p className="tif-description">
          Answer the following questions to configure your assessment.
        </p>
      </div>

      {/* Progress */}
      <div className="tif-progress">
        <div className="tif-progress-bar">
          <div
            className="tif-progress-fill"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
        <span className="tif-progress-text">
          {progress.answered} of {progress.total} required questions answered
        </span>
      </div>

      {/* Questions */}
      <div className="tif-questions">
        {template.intake_questions.map(renderQuestion)}
      </div>

      {/* Actions */}
      <div className="tif-actions">
        <button
          type="button"
          className="tif-btn tif-btn--cancel"
          onClick={onCancel}
          disabled={isSubmitting || validating}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="tif-btn tif-btn--submit"
          disabled={isSubmitting || validating || progress.answered < progress.total}
        >
          {isSubmitting || validating ? 'Processing...' : 'Continue'}
        </button>
      </div>
    </form>
  );
};

export default TemplateIntakeForm;
